import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, Lock, LogOut, Save, Upload, Eye, Mail, FileText, ExternalLink, ArrowLeft, Menu, X, Image
} from "lucide-react";
import { FaLinkedinIn, FaInstagram } from "react-icons/fa";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("content");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const logoInputRef = useRef(null);
  const resumeInputRef = useRef(null);

  const [settings, setSettings] = useState({
    headline: "", subheadline: "", email: "", linkedin_url: "", instagram_url: "",
    logo_url: "", resume_url: "", show_email: true, show_linkedin: true, show_instagram: true, show_resume: true,
    banner_top: "", banner_bottom: "", show_banner_top: false, show_banner_bottom: false
  });

  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken")}` });

  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem("adminToken");
      if (!token) { navigate("/admin"); return; }
      try {
        await axios.get(`${API}/auth/verify`, { headers: getAuthHeaders() });
        fetchSettings();
      } catch (error) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
      }
    };
    verifyAuth();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: getAuthHeaders() });
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      setSettings({ ...settings, logo_url: response.data.logo_url });
      toast.success("Logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload logo");
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/upload/resume`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      setSettings({ ...settings, resume_url: response.data.resume_url });
      toast.success("Resume uploaded!");
    } catch (error) {
      toast.error("Failed to upload resume");
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, { headers: getAuthHeaders() });
      toast.success("Password changed!");
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error("Failed to change password");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#183153] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const menuItems = [
    { id: "content", label: "Content", icon: LayoutDashboard },
    { id: "links", label: "Links", icon: ExternalLink },
    { id: "visibility", label: "Visibility", icon: Eye },
    { id: "banners", label: "Banners", icon: Image },
    { id: "password", label: "Password", icon: Lock }
  ];

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="md:hidden bg-[#183153] text-white px-4 py-3 flex items-center justify-between">
        <Link to="/" className="text-white/80 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
        <h1 className="font-display text-lg">Admin Dashboard</h1>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0F2F4F] text-white">
          {menuItems.map((item) => (
            <button key={item.id} onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
              className={`w-full px-4 py-3 flex items-center gap-3 ${activeTab === item.id ? "bg-white/10" : ""}`}>
              <item.icon className="w-4 h-4" /><span>{item.label}</span>
            </button>
          ))}
          <button onClick={handleLogout} className="w-full px-4 py-3 flex items-center gap-3 text-red-300 border-t border-white/10">
            <LogOut className="w-4 h-4" /><span>Logout</span>
          </button>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex admin-sidebar w-60 min-h-screen flex-col text-white" style={{background: 'linear-gradient(180deg, #183153 0%, #0F2F4F 100%)'}}>
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" /><span className="text-sm">Back to site</span>
            </Link>
            <h1 className="font-display text-xl">Dashboard</h1>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => (
              <button key={item.id} onClick={() => setActiveTab(item.id)}
                className={`w-full px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${activeTab === item.id ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
                <item.icon className="w-4 h-4" /><span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-white/10">
            <button onClick={handleLogout} className="w-full px-4 py-3 rounded-lg flex items-center gap-3 text-white/60 hover:bg-white/5 hover:text-red-300">
              <LogOut className="w-4 h-4" /><span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 max-w-4xl">
          
          {/* Content Tab */}
          {activeTab === "content" && (
            <div className="space-y-6">
              <div><h2 className="font-display text-2xl text-[#0F2F4F]">Site Content</h2></div>
              <Card>
                <CardHeader><CardTitle>Logo</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {settings.logo_url && <div className="bg-gray-50 p-4 rounded-lg"><img src={settings.logo_url} alt="Logo" className="h-20 w-auto mx-auto" /></div>}
                  <input type="file" accept="image/*" ref={logoInputRef} onChange={handleLogoUpload} className="hidden" />
                  <Button variant="outline" onClick={() => logoInputRef.current?.click()} className="w-full border-dashed border-2">
                    <Upload className="w-4 h-4 mr-2" />Upload Logo
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Text Content</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>Headline</Label><Input value={settings.headline} onChange={(e) => setSettings({...settings, headline: e.target.value})} /></div>
                  <div><Label>Subheadline</Label><Textarea value={settings.subheadline} onChange={(e) => setSettings({...settings, subheadline: e.target.value})} /></div>
                </CardContent>
              </Card>
              <Button onClick={handleSaveSettings} className="w-full bg-[#183153]" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}

          {/* Links Tab */}
          {activeTab === "links" && (
            <div className="space-y-6">
              <div><h2 className="font-display text-2xl text-[#0F2F4F]">Links & Contact</h2></div>
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><Mail className="w-4 h-4"/>Email</CardTitle></CardHeader>
                <CardContent><Input type="email" value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} /></CardContent>
              </Card>
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><FaLinkedinIn/>LinkedIn</CardTitle></CardHeader>
                <CardContent><Input value={settings.linkedin_url} onChange={(e) => setSettings({...settings, linkedin_url: e.target.value})} /></CardContent>
              </Card>
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><FaInstagram/>Instagram</CardTitle></CardHeader>
                <CardContent><Input value={settings.instagram_url} onChange={(e) => setSettings({...settings, instagram_url: e.target.value})} /></CardContent>
              </Card>
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-4 h-4"/>Resume</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {settings.resume_url && <div className="p-3 bg-green-50 text-green-700 rounded">Resume uploaded</div>}
                  <input type="file" accept=".pdf" ref={resumeInputRef} onChange={handleResumeUpload} className="hidden" />
                  <Button variant="outline" onClick={() => resumeInputRef.current?.click()} className="w-full border-dashed border-2">
                    <Upload className="w-4 h-4 mr-2" />{settings.resume_url ? "Replace" : "Upload"} Resume
                  </Button>
                </CardContent>
              </Card>
              <Button onClick={handleSaveSettings} className="w-full bg-[#183153]" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}

          {/* Visibility Tab */}
          {activeTab === "visibility" && (
            <div className="space-y-6">
              <div><h2 className="font-display text-2xl text-[#0F2F4F]">Button Visibility</h2></div>
              <Card><CardContent className="pt-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 bg-[#183153] rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-white"/></div><div><p className="font-medium">Email Me</p></div></div>
                  <Switch checked={settings.show_email} onCheckedChange={(checked) => setSettings({...settings, show_email: checked})} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 bg-[#183153] rounded-lg flex items-center justify-center"><FaLinkedinIn className="w-5 h-5 text-white"/></div><div><p className="font-medium">LinkedIn</p></div></div>
                  <Switch checked={settings.show_linkedin} onCheckedChange={(checked) => setSettings({...settings, show_linkedin: checked})} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 bg-[#183153] rounded-lg flex items-center justify-center"><FaInstagram className="w-5 h-5 text-white"/></div><div><p className="font-medium">Instagram</p></div></div>
                  <Switch checked={settings.show_instagram} onCheckedChange={(checked) => setSettings({...settings, show_instagram: checked})} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 bg-[#183153] rounded-lg flex items-center justify-center"><FileText className="w-5 h-5 text-white"/></div><div><p className="font-medium">Resume</p></div></div>
                  <Switch checked={settings.show_resume} onCheckedChange={(checked) => setSettings({...settings, show_resume: checked})} />
                </div>
              </CardContent></Card>
              <Button onClick={handleSaveSettings} className="w-full bg-[#183153]" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}

          {/* Banners Tab */}
          {activeTab === "banners" && (
            <div className="space-y-6">
              <div>
                <h2 className="font-display text-2xl text-[#0F2F4F]">Ad Banners</h2>
                <p className="text-gray-500 mt-1">Paste your AdSense code or custom banner HTML</p>
              </div>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Top Banner</CardTitle>
                    <Switch checked={settings.show_banner_top} onCheckedChange={(checked) => setSettings({...settings, show_banner_top: checked})} />
                  </div>
                  <CardDescription>Displayed above your logo</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea 
                    placeholder="Paste your AdSense code or banner HTML here..."
                    value={settings.banner_top || ""} 
                    onChange={(e) => setSettings({...settings, banner_top: e.target.value})}
                    className="min-h-[120px] font-mono text-sm"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Bottom Banner</CardTitle>
                    <Switch checked={settings.show_banner_bottom} onCheckedChange={(checked) => setSettings({...settings, show_banner_bottom: checked})} />
                  </div>
                  <CardDescription>Displayed above the footer</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea 
                    placeholder="Paste your AdSense code or banner HTML here..."
                    value={settings.banner_bottom || ""} 
                    onChange={(e) => setSettings({...settings, banner_bottom: e.target.value})}
                    className="min-h-[120px] font-mono text-sm"
                  />
                </CardContent>
              </Card>

              <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Tips:</strong><br/>
                • For AdSense: Copy the full ad code from Google AdSense<br/>
                • For custom banners: Use HTML like <code>&lt;a href="..."&gt;&lt;img src="..."&gt;&lt;/a&gt;</code>
              </div>

              <Button onClick={handleSaveSettings} className="w-full bg-[#183153]" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === "password" && (
            <div className="space-y-6">
              <div><h2 className="font-display text-2xl text-[#0F2F4F]">Change Password</h2></div>
              <Card><CardContent className="pt-6 space-y-4">
                <div><Label>Current Password</Label><Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({...passwords, current_password: e.target.value})} /></div>
                <div><Label>New Password</Label><Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({...passwords, new_password: e.target.value})} /></div>
                <div><Label>Confirm Password</Label><Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})} /></div>
              </CardContent></Card>
              <Button onClick={handleChangePassword} className="w-full bg-[#183153]" disabled={!passwords.current_password || !passwords.new_password}>
                <Lock className="w-4 h-4 mr-2" />Change Password
              </Button>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
